import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useGetSchedulingSlotsQuery } from '@/api/hooks/queries/use-get-scheduling-slots-query';
import { Button } from '@/components/ui/button';
import { useFeatureFlagVariantState } from '@/hooks/use-feature-flag-state';
import { useRunOnce } from '@/hooks/use-run-once';
import {
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  TELEHEALTH_CONTACT_TYPE,
  type TelehealthContactType,
} from '@/lib/constants';
import { logger } from '@/lib/logger';
import {
  mergeSchedulingSlotsByDate,
  resolveSchedulingSlotConflictPreference,
  type SchedulingSlotConflictPreference,
} from '@/lib/scheduling-slots';
import { isPostHogEnabled } from '@/posthog';
import type { SchedulingSlot, SchedulingSlotDateTime } from '@/types/scheduling.type';
import { DateCarousel } from '@/views/schedule-appointment/components/DateCarousel';
import { TimeSlotGrid } from '@/views/schedule-appointment/components/TimeSlotGrid';

import { CardContent } from './ui/card';

const SCHEDULING_SLOT_CONTACT_PREFERENCE_FLAG = 'scheduling-slot-contact-preference';

interface AvailabilityCalendarProps {
  startDate: Date;
  endDate: Date;
  licensedInState: string | null;
  onSubmit: (data: AvailabilityCalendarFormData) => void | Promise<void>;
  appointmentTypeId: string;
  contactType: TelehealthContactType;
  additionalAppointmentTypeId?: string;
  additionalContactType?: TelehealthContactType;
}

const telehealthContactTypeSchema = z.enum([
  TELEHEALTH_CONTACT_TYPE.VIDEO,
  TELEHEALTH_CONTACT_TYPE.PHONE,
]);

const availabilityCalendarFormSchema = z.object({
  selectedDate: z.date({ error: 'Please select a date' }),
  selectedSlot: z.string({ error: 'Please select a time slot' }),
  selectedProviderId: z.string().optional(),
  selectedContactType: telehealthContactTypeSchema.optional(),
  selectedAppointmentTypeId: z.string().optional(),
});

export type AvailabilityCalendarFormData = z.infer<typeof availabilityCalendarFormSchema>;

function getTimeSlotsFromSelectedDate(
  slotsData: SchedulingSlot[] | undefined,
  selectedDate: Date | undefined
): SchedulingSlotDateTime[] {
  if (!selectedDate || !slotsData) return [];

  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const dateSlotData = slotsData.find((slot) => slot.date === dateKey);
  return dateSlotData?.slots ?? [];
}

export const AvailabilityCalendar = ({
  startDate,
  endDate,
  licensedInState,
  onSubmit,
  appointmentTypeId,
  contactType,
  additionalAppointmentTypeId,
  additionalContactType,
}: AvailabilityCalendarProps) => {
  const schedulingSlotsQuery = useGetSchedulingSlotsQuery(
    {
      start: startDate,
      end: endDate,
      licensedInState,
      contactType,
      appointmentTypeId,
    },
    {
      enabled: !!appointmentTypeId,
    }
  );
  const schedulingSlots = schedulingSlotsQuery.data;

  const additionalSchedulingSlotsQuery = useGetSchedulingSlotsQuery(
    {
      start: startDate,
      end: endDate,
      licensedInState,
      contactType: additionalContactType ?? DEFAULT_TELEHEALTH_CONTACT_TYPE,
      appointmentTypeId: additionalAppointmentTypeId ?? '',
    },
    {
      enabled: !!additionalAppointmentTypeId,
    }
  );

  const additionalSchedulingSlots = additionalSchedulingSlotsQuery.data;
  const hasAdditionalAppointmentType = !!additionalAppointmentTypeId;

  const conflictPreferenceFlag = useFeatureFlagVariantState(
    SCHEDULING_SLOT_CONTACT_PREFERENCE_FLAG
  );

  // Only resolve (and wait on) PostHog when a second source can conflict. Unread variants
  // look like `undefined` and would resolve to `video`, then flip and clear a selection.
  // Single-source callers (e.g. reschedule) skip the flag gate entirely.
  const conflictPreference = useMemo((): SchedulingSlotConflictPreference | null => {
    if (!hasAdditionalAppointmentType) {
      return null;
    }

    if (!isPostHogEnabled) {
      return import.meta.env.DEV ? 'random' : 'video';
    }

    if (conflictPreferenceFlag === 'loading') {
      return null;
    }

    return resolveSchedulingSlotConflictPreference(conflictPreferenceFlag.variant);
  }, [conflictPreferenceFlag, hasAdditionalAppointmentType]);

  const isConflictPreferenceLoading = hasAdditionalAppointmentType && conflictPreference === null;

  const mergedSchedulingSlots = useMemo(() => {
    if (!hasAdditionalAppointmentType) {
      return schedulingSlots ?? [];
    }

    if (!conflictPreference) {
      return [];
    }

    return mergeSchedulingSlotsByDate(schedulingSlots, additionalSchedulingSlots, {
      conflictPreference,
    });
  }, [
    schedulingSlots,
    additionalSchedulingSlots,
    conflictPreference,
    hasAdditionalAppointmentType,
  ]);

  const isSlotsLoading =
    schedulingSlotsQuery.isLoading ||
    additionalSchedulingSlotsQuery.isLoading ||
    isConflictPreferenceLoading;

  const shouldLogNoSlots =
    !isSlotsLoading && !!appointmentTypeId && mergedSchedulingSlots.length === 0;

  useRunOnce(function logNoSchedulingSlotsFound() {
    logger.error(new Error('No scheduling slots found'));
  }, shouldLogNoSlots);

  const form = useForm<AvailabilityCalendarFormData>({
    resolver: zodResolver(availabilityCalendarFormSchema),
    defaultValues: {
      selectedDate: undefined,
      selectedSlot: undefined,
      selectedProviderId: undefined,
      selectedContactType: undefined,
      selectedAppointmentTypeId: undefined,
    },
  });

  const selectedDate = form.watch('selectedDate');
  const availableTimeSlots = useMemo(
    () => getTimeSlotsFromSelectedDate(mergedSchedulingSlots, selectedDate),
    [selectedDate, mergedSchedulingSlots]
  );

  useEffect(
    function resetSelectedSlotOnSchedulingSlotDataChange() {
      form.resetField('selectedSlot');
      form.resetField('selectedProviderId');
      form.resetField('selectedContactType');
      form.resetField('selectedAppointmentTypeId');
    },
    [mergedSchedulingSlots, form]
  );

  useEffect(
    function autoSelectFirstDateWithSlots() {
      if (isSlotsLoading || selectedDate) {
        return;
      }

      const firstDateRow = mergedSchedulingSlots.find((row) => row.slots.length > 0);
      if (!firstDateRow) {
        return;
      }

      form.setValue('selectedDate', parseISO(`${firstDateRow.date}T00:00:00`));
    },
    [selectedDate, mergedSchedulingSlots, isSlotsLoading, form]
  );

  const handleOnSubmit = (data: AvailabilityCalendarFormData) => {
    return onSubmit(data);
  };

  return (
    <CardContent className="flex flex-col gap-6">
      <form onSubmit={form.handleSubmit(handleOnSubmit)} className="flex flex-col gap-6">
        <Controller
          name="selectedDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <div>
              <DateCarousel
                selectedDate={field.value}
                slotsData={mergedSchedulingSlots}
                onDateSelect={(date) => {
                  field.onChange(date);
                  form.resetField('selectedSlot');
                  form.resetField('selectedProviderId');
                  form.resetField('selectedContactType');
                  form.resetField('selectedAppointmentTypeId');
                }}
              />
              {fieldState.error && (
                <p className="mt-2 text-sm text-destructive">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        {isSlotsLoading && (
          <div className="py-8 text-center text-muted-foreground">Loading available slots...</div>
        )}

        {!isSlotsLoading && availableTimeSlots.length === 0 && (
          <div className="py-8 text-center text-muted-foreground">
            No available slots for this date
          </div>
        )}

        {availableTimeSlots.length > 0 && (
          <Controller
            name="selectedSlot"
            control={form.control}
            render={({ field, fieldState }) => (
              <div>
                <TimeSlotGrid
                  slots={availableTimeSlots}
                  selectedSlot={field.value}
                  onSlotSelect={(slot) => {
                    field.onChange(slot.start);
                    form.setValue('selectedProviderId', slot.providerId);
                    form.setValue('selectedContactType', slot.contactType);
                    form.setValue('selectedAppointmentTypeId', slot.appointmentTypeId);
                  }}
                />
                {fieldState.error && (
                  <p className="mt-2 text-sm text-destructive">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        )}

        <Button
          type="submit"
          disabled={
            form.formState.isSubmitting || isSlotsLoading || availableTimeSlots.length === 0
          }
          className="w-full"
        >
          {form.formState.isSubmitting ? 'Submitting...' : 'Continue'}
        </Button>
      </form>
    </CardContent>
  );
};
